<template>
  <div class="elevation-3 ma-3 pa-3">
    <v-row>
      <v-col cols="1" class="mt-2"/>
      <v-col cols="10" class="pt-0 pb-0">
        <!-- Tax Year and Company share a line. Both hold short values, and pairing them
             reads as the one question they are - which county year, for which company -
             while leaving the three file controls below at full width, where the FTP
             file names actually need it. -->
        <v-row class="ma-0">
          <v-col cols="6" class="pa-0 pr-2">
            <CommonDropDowns :isItTaxYearWithoutAllYears=true
            :taxYearValue="taxYear"
            @change="dataFromCommonDropDowns" />
          </v-col>
          <v-col cols="6" class="pa-0 pl-2">
            <!-- Company picks the FTP folder below, and names the run on the imported rows
                 and the Error List. It says nothing about how the file is read - that is
                 Match By. -->
            <v-select :items="companies" class="input-text-size"
            label="Company"
            v-model="selectedCompany"
            @change="onCompanyChange"
            clearable/>
          </v-col>
        </v-row>
        <!-- no-data-text carries why the list is empty. A blank dropdown with the
             stock "No data available" is exactly what TKT-000856 was: the FTP server
             was unreachable and nothing on screen said so. -->
        <v-select :items="ftpFiles" class="input-text-size mt-n4"
        item-text="label" item-value="name" label="Select FTP File"
        v-model="selectedFtpFile"
        :loading="loadingFtpFiles"
        :disabled="!selectedCompany || loadingFtpFiles"
        :no-data-text="ftpNoDataText"
        clearable/>
        <!-- Whether the key in the first 28 characters of each line is a tax ID or a
             parcel ID. Parcel IDs are handled with or without dashes by the same
             option - the import strips them before matching the roll. -->
        <v-select :items="matchByOptions" class="input-text-size mt-n4"
        item-text="text" item-value="value" label="Match By"
        v-model="selectedMatchBy"
        @change="onMatchByChange"
        clearable/>
      </v-col>
      <v-col cols="12" class="pt-0 pb-0 mt-n11">
          <CommonImportFiles @loadCRUDList="loadBillRequestAfterImport"
          @fileChanged="localFileChosen = $event"
          :dynamicUpdateInputs="dynamicUpdateInputsValues"
          :disabled="!readyToImport"
          :fileResolver="resolveFtpFile" />
      </v-col>
      <v-col cols="12" class="pt-2 pb-0 mt-n5">
        <v-row>
          <v-col cols="6" class="pt-2 pb-0">
            <label class="input-text-size">Total Records in File :</label>
          </v-col>
          <v-col cols="6" class="pt-0 pb-0">
            <v-text-field class="input-text-size pa-0"
            :value="totalResults.totalRecords" readonly hide-details />
          </v-col>
          <v-col cols="6" class="pt-2 pb-0">
            <label class="input-text-size">Matches :</label>
          </v-col>
          <v-col cols="6" class="pt-0 pb-0">
            <v-text-field class="input-text-size pa-0" :value="totalResults.matches"
            readonly hide-details />
          </v-col>
          <v-col cols="6" class="pt-2 pb-0">
            <label class="input-text-size">Mis-matched: </label>
          </v-col>
          <v-col cols="6" class="pt-0 pb-0">
            <v-text-field class="input-text-size pa-0" readonly
            :value="totalResults.errors" hide-details />
          </v-col>
          <v-col cols="6" class="pt-2 pb-0">
            <label class="input-text-size">Records with Mtg code: </label>
          </v-col>
          <v-col cols="6" class="pt-0 pb-0">
            <v-text-field class="input-text-size pa-0" readonly
            :value="totalResults.recordsWithMtgCode" hide-details />
          </v-col>
        </v-row>
      </v-col>
    <v-row>
      <v-col cols="6">
        <v-checkbox class="input-text-size radio-and-checekbox-size" v-model="checkboxForMismatch"
        label="Show mis-matched Only" @change="loadBillRequestAfterImport" />
      </v-col>
      <v-col cols="6">
        <v-checkbox class="input-text-size radio-and-checekbox-size"
        v-model="checkboxForMismatchAndRecWithMtgCode"
        label="Show mis-matched and records with mtg code only"
        @change="loadBillRequestAfterImport" />
      </v-col>
      <v-col class="mt-n10 pt-0 d-flex justify-center">
        <v-btn class="input-text-size" text color="blue darken-1" @click="printErrorList">
          Print Error List
        </v-btn>
      </v-col>
    </v-row>
    </v-row>
  </div>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import CommonImportFiles from '../../common/import/CommonImportFiles.vue';
import {
  ftpEmptyMessage,
  ftpErrorMessage,
  readFtpFileList,
  readFtpPath,
} from '../../../util/shared/ftp-file-list';
import { transformMapperListFields } from '../../../util/shared/transform';
import CommonDropDowns from '../common-utils/CommonDropDowns.mixin.vue';
import { generateQueryString } from '../../common/base/report/report-util';
import { clients } from '../../../util/clients';
import '../../../assets/style.css';

const { backendRest } = clients.direct;

// The backend hands the file back as base64 so the bytes survive the trip - these
// are fixed-width / delimited files where an ascii round-trip could shift a column.
// Rebuilt into a File here so it is indistinguishable from a local pick downstream.
const base64ToFile = (base64, fileName) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], fileName, { type: 'text/plain' });
};

// The two companies bill request files come from. The value is sent to the FTP
// endpoints, which turn it into a folder (/CL/BILLREQUEST, /LERETA/BILLREQUEST), and is
// stamped onto every imported row as mtgCompany - the profiles no longer name a company,
// so this is the only thing the Error List can be named after.
const COMPANIES = ['CoreLogic', 'Lereta'];

/* How to read the key in the first 28 characters of each line. Both companies send both
   styles, so each option maps to one company-neutral profile. (28, not 36: Lereta writes a
   company code at columns 32-36, which a 36 wide key swallowed - see the profile seed.)
   `destination` is a fallback for finding a county's own renamed copy of a profile, now
   that there is no dropdown to pick it with by hand. */
const MATCH_BY_OPTIONS = [
  {
    text: 'By Tax ID',
    value: 'taxId',
    profileName: 'Bill Request by Tax ID',
    destination: 'importTaxMortgageCoProcessing',
  },
  {
    // Dashed and undashed alike - the import strips dashes before matching the roll,
    // which is why this is one option and not two.
    text: 'By Parcel ID',
    value: 'parcelId',
    profileName: 'Bill Request by Parcel ID',
    destination: 'importTaxMortgageCoProcessingByParcelId',
  },
];

export default {
  name: 'MtgCoBillRequestsSideBar',
  components: {
    CommonImportFiles,
    CommonDropDowns,
  },
  data() {
    return {
      checkboxForMismatch: false,
      checkboxForMismatchAndRecWithMtgCode: false,
      baseCrudKeyForMappers: 'mtg-bill-request-mapper-list',
      companies: COMPANIES,
      matchByOptions: MATCH_BY_OPTIONS,
      taxYear: 0,
      selectedCompany: null,
      selectedMatchBy: null,
      ftpFiles: [],
      selectedFtpFile: null,
      loadingFtpFiles: false,
      // Why the file list is empty, shown inside the dropdown itself. Null until a
      // company has been picked, when "No files found" would be misleading.
      ftpListMessage: null,
      localFileChosen: false,
    };
  },
  created() {
    this.loadMappers();
  },
  computed: {
    ...mapGetters('base/crud', [
      'criteria',
      'items',
    ]),
    ...mapState({
      latestTaxYear: (state) => state.tax.taxCommon.latestTaxYear,
      taxSettingItem: (state) => state.SystemConfig.taxSettingItem || {},
      mortgageCoMapperItems: (state) => state.tax.MortgageCoProcessing.mortgageCompanyMapperItems,
      totalResults: (state) => state.tax.MortgageCoProcessing.totalResults,
    }),
    // Same default the tax year dropdown falls back to (CommonDropDowns). Needed in
    // full here, not just on screen: the dropdown shows that default but never emits
    // it, and Import now waits on a tax year actually being set.
    defaultTaxYear() {
      return Number(this.taxSettingItem.taxYear) || this.latestTaxYear;
    },
    getMappersItems() {
      const itemData = this.items(this.baseCrudKeyForMappers);
      return itemData;
    },
    // Stamped onto every row of the file. The tax year has always been sent this way;
    // the company joins it because the profiles are company-neutral now, so without it
    // nothing on the record would say which company the run was for and the Error List
    // would lose its name.
    dynamicUpdateInputsValues() {
      const inputs = [{ type: 'constant', prop: 'mtgCompanyTaxYear', value: this.taxYear }];
      if (this.selectedCompany) {
        inputs.push({ type: 'constant', prop: 'mtgCompany', value: this.selectedCompany });
      }
      return inputs;
    },
    ftpNoDataText() {
      if (this.loadingFtpFiles) return 'Reading the FTP server...';
      if (this.ftpListMessage) return this.ftpListMessage;
      return 'Select a company to list its files';
    },
    // Every required selection made, and a file to import from somewhere.
    readyToImport() {
      return Boolean(this.taxYear)
        && Boolean(this.selectedCompany)
        && Boolean(this.selectedMatchBy)
        && Boolean(this.selectedFtpFile || this.localFileChosen);
    },
  },
  watch: {
    // The default arrives after mount (config + tax year list load async), so seed
    // whenever it lands and nothing has been picked yet.
    defaultTaxYear: {
      immediate: true,
      handler(year) {
        if (year && !this.taxYear) {
          this.dataFromCommonDropDowns(year);
        }
      },
    },
    // The profile list is fetched on created(), so a Match By picked before it lands
    // would have found nothing. Resolve again whenever it changes.
    getMappersItems() {
      if (this.selectedMatchBy) this.applyMatchByProfile(this.selectedMatchBy);
    },
  },
  methods: {
    ...mapActions('config', [
      'loadTransformMappers',
    ]),
    ...mapMutations('tax/MortgageCoProcessing', [
      'setPipeline',
    ]),
    ...mapActions(['reAuth', 'flashError']),
    dataFromCommonDropDowns(val) {
      this.taxYear = val;
    },
    loadMappers() {
      if (!this.getMappersItems.length) {
        this.loadTransformMappers({
          criteria: { type: 'import', program: 'MTGBILLREQUESTS' },
          fields: { ...transformMapperListFields },
          baseCrudKey: this.baseCrudKeyForMappers,
        });
      }
    },
    onCompanyChange() {
      this.loadFtpFiles();
    },
    onMatchByChange(val) {
      this.applyMatchByProfile(val);
    },
    /* Match By no longer names a profile on screen, so find the one it means. By name
       first, which is what the seeded profiles are called; by destination second, so a
       county running its own renamed copy still imports - there is no dropdown left for
       it to pick that copy with by hand. */
    applyMatchByProfile(val) {
      const option = MATCH_BY_OPTIONS.find((o) => o.value === val);
      if (!option) {
        this.setPipeline({});
        return;
      }
      const mappers = this.getMappersItems || [];
      // Still loading - the watcher below runs this again when the list lands, rather
      // than telling the county a profile is missing that simply has not arrived yet.
      if (!mappers.length) {
        this.setPipeline({});
        return;
      }
      const profile = mappers.find((m) => m.name === option.profileName)
        || mappers.find((m) => m.destination === option.destination);
      if (!profile) {
        this.flashError(`The "${option.profileName}" import profile is missing. Ask TMC to re-seed it.`);
      }
      this.setPipeline(profile || {});
    },
    async loadFtpFiles() {
      // Changing the company changes the remote folder, so anything already picked
      // belongs to the old one and is dropped.
      this.ftpFiles = [];
      this.selectedFtpFile = null;
      this.ftpListMessage = null;
      if (!this.selectedCompany) return;
      this.loadingFtpFiles = true;
      try {
        await this.reAuth();
        const response = await backendRest.get('/mortgage/bill-request/ftp/files', {
          params: { company: this.selectedCompany },
        });
        this.ftpFiles = readFtpFileList(response.data);
        if (!this.ftpFiles.length) {
          // The server answered and the folder is genuinely empty - say which folder,
          // so a county looking in the wrong place can see that it is.
          this.ftpListMessage = ftpEmptyMessage(readFtpPath(response.data));
        }
      } catch (err) {
        console.error('Error loading bill request FTP files', err);
        // The reason the FTP server gave, not a generic "unable to read" - the
        // whole of TKT-000856 was that nobody could tell a dead connection from
        // an empty folder.
        this.ftpListMessage = ftpErrorMessage(err);
        this.flashError(this.ftpListMessage);
      } finally {
        this.loadingFtpFiles = false;
      }
    },
    // Handed to CommonImportFiles and called only when Import is clicked with no
    // local file chosen. Selecting from the picker records a name and nothing more -
    // the file is fetched at import time, so browsing the list costs no transfers.
    async resolveFtpFile() {
      const fileName = this.selectedFtpFile;
      if (!fileName) return null;
      try {
        await this.reAuth();
        const response = await backendRest.get('/mortgage/bill-request/ftp/file', {
          params: { company: this.selectedCompany, fileName },
        });
        const { base64 } = response.data || {};
        if (!base64) throw new Error('empty response');
        return base64ToFile(base64, fileName);
      } catch (err) {
        console.error('Error reading bill request FTP file', err);
        this.flashError(`Unable to read "${fileName}" from the FTP server.`);
        return null;
      }
    },
    loadBillRequestAfterImport() {
      this.$emit('loadCRUDList', this.checkboxForMismatch, this.checkboxForMismatchAndRecWithMtgCode);
    },
    async printErrorList() {
      const params = await this.getParamsData();
      const queryString = generateQueryString(params);
      const url = `${backendRest.defaults.baseURL}/mortgageCoProcessing/printErrorList${queryString}`;
      window.open(url, '_blank');
    },
    async getParamsData() {
      const { jwt } = await this.reAuth();
      const getcriteria = {
        ...this.criteriaState,
      };
      const params = [];
      if (this.devSetting && this.devSetting.isTestEnvironment) {
        params.push('isTestCafe=1');
      }
      params.push(`token=${jwt}`);
      const encodedCriteria = encodeURIComponent(JSON.stringify(getcriteria));
      params.push(`filters=${encodedCriteria}`);
      return params;
    },
  },
};
</script>
